import { CommonModule, KeyValue } from '@angular/common';
import { Component, AfterViewInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { NgbDate, NgbModule } from '@ng-bootstrap/ng-bootstrap';

// Components
import { AlertComponent } from '../../../../../../shared/_components/alert/alert.component';
import { ButtonComponent } from '../../../../../../shared/_components/button/button.component';
import { CheckboxComponent } from '../../../../../../shared/_components/checkbox/checkbox.component';
import { DatePickerComponent } from '../../../../../../shared/_components/date-picker/date-picker.component';
import { DropdownComponent } from '../../../../../../shared/_components/dropdown/dropdown.component';
import { RadioButtonComponent } from '../../../../../../shared/_components/radio-button/radio-button.component';
import { TextAreaComponent } from '../../../../../../shared/_components/text-area/text-area.component';
import { FlowMediaUploadStep } from '../../flows/flow-media-upload/step/flow-media-upload-step.component';
import { PostPreviewComponent } from '../../post-preview/post-preview.component';

// Models
import { IntegrationProvider } from '../../../../../models/integration-providers.model';

// Schemas
import { YouTubeVisibility as Visibility } from '@oddr/user-schemas';

const HOUR_LIST = [
  { key: '12', value: '12' },
  { key: '01', value: '1' },
  { key: '02', value: '2' },
  { key: '03', value: '3' },
  { key: '04', value: '4' },
  { key: '05', value: '5' },
  { key: '06', value: '6' },
  { key: '07', value: '7' },
  { key: '08', value: '8' },
  { key: '09', value: '9' },
  { key: '10', value: '10' },
  { key: '11', value: '11' },
] as KeyValue<string, string>[];

const MINUTE_LIST = [
  { key: '00', value: '00' },
  { key: '01', value: '01' },
  { key: '02', value: '02' },
  { key: '03', value: '03' },
  { key: '04', value: '04' },
  { key: '05', value: '05' },
  { key: '06', value: '06' },
  { key: '07', value: '07' },
  { key: '08', value: '08' },
  { key: '09', value: '09' },
  { key: '10', value: '10' },
  { key: '11', value: '11' },
  { key: '12', value: '12' },
  { key: '13', value: '13' },
  { key: '14', value: '14' },
  { key: '15', value: '15' },
  { key: '16', value: '16' },
  { key: '17', value: '17' },
  { key: '18', value: '18' },
  { key: '19', value: '19' },
  { key: '20', value: '20' },
  { key: '21', value: '21' },
  { key: '22', value: '22' },
  { key: '23', value: '23' },
  { key: '24', value: '24' },
  { key: '25', value: '25' },
  { key: '26', value: '26' },
  { key: '27', value: '27' },
  { key: '28', value: '28' },
  { key: '29', value: '29' },
  { key: '30', value: '30' },
  { key: '31', value: '31' },
  { key: '32', value: '32' },
  { key: '33', value: '33' },
  { key: '34', value: '34' },
  { key: '35', value: '35' },
  { key: '36', value: '36' },
  { key: '37', value: '37' },
  { key: '38', value: '38' },
  { key: '39', value: '39' },
  { key: '40', value: '40' },
  { key: '41', value: '41' },
  { key: '42', value: '42' },
  { key: '43', value: '43' },
  { key: '44', value: '44' },
  { key: '45', value: '45' },
  { key: '46', value: '46' },
  { key: '47', value: '47' },
  { key: '48', value: '48' },
  { key: '49', value: '49' },
  { key: '50', value: '50' },
  { key: '51', value: '51' },
  { key: '52', value: '52' },
  { key: '53', value: '53' },
  { key: '54', value: '54' },
  { key: '55', value: '55' },
  { key: '56', value: '56' },
  { key: '57', value: '57' },
  { key: '58', value: '58' },
  { key: '59', value: '59' },
] as KeyValue<string, string>[];

const PERIOD_LIST = [
  { key: 'am', value: 'AM' },
  { key: 'pm', value: 'PM' },
] as KeyValue<string, string>[];

@Component({
  selector: 'pp-step-integration-youtube',
  templateUrl: './step-integration-youtube.component.html',
  imports: [CommonModule, NgbModule, AlertComponent, ButtonComponent, CheckboxComponent, DatePickerComponent, DropdownComponent, PostPreviewComponent, RadioButtonComponent, TextAreaComponent],
})
export class StepIntegrationYoutubeComponent extends FlowMediaUploadStep implements AfterViewInit {

  // Component handler (!IMPORTANT - ytSt match step identifier for this flow)
  identifier = 'mediaUploadFlowStepIntegrationYoutube';

  // Aliases
  visibility = Visibility;

  // Post schedule handlers
  scheduleCollapseAnimationTime = 350;
  scheduleInCollapsedState = true;
  scheduleIsCollapsing = false;

  // Upload options
  timezones: KeyValue<string, string>[] = [];
  visibilityType = 'Private';

  constructor() {
    super();

    // Define form
    const dateNow = new Date();
    this.form = new FormGroup({
      ytPostTitle: new FormControl<string>(''),
      ytPostDescription: new FormControl<string>('', Validators.required),
      ytPostMadeForKids: new FormControl<boolean>(false),
      ytIncludeOddrLink: new FormControl<boolean>(true),
      ytPostScheduleDate: new FormControl<string | null>(null),
      ytPostVisibility: new FormControl<Visibility>(Visibility.Private),
      ytManualSchedule: new FormControl<boolean>(false),
      ytScheduledYear: new FormControl<number>(0),
      ytScheduledMonth: new FormControl<number>(0),
      ytScheduledDay: new FormControl<number>(0),
      ytScheduledHour: new FormControl<KeyValue<string, string> | null>(null),
      ytScheduledMinute: new FormControl<KeyValue<string, string> | null>(null),
      ytScheduledPeriod: new FormControl<KeyValue<string, string> | null>(null),
      ytScheduledTimezone: new FormControl<KeyValue<string, string> | null>(null),
    });

    // Set times in form to current time
    const dateNowHourSelection = (dateNow.getHours() < 12) ?
      HOUR_LIST.find(i => i.key === (dateNow.getHours()).toString().padStart(2, '0')) :
      HOUR_LIST.find(i => i.key === (dateNow.getHours() - 12).toString().padStart(2, '0'));
    const dateNowMinuteSelection = MINUTE_LIST.find(i => i.key === dateNow.getMinutes().toString());
    const dateNowTimePeriod = (dateNow.getHours() < 12) ?
      PERIOD_LIST[0] :
      PERIOD_LIST[1];
    this.form.get('ytScheduledYear')?.setValue(dateNow.getFullYear());
    this.form.get('ytScheduledMonth')?.setValue(dateNow.getMonth());
    this.form.get('ytScheduledDay')?.setValue(dateNow.getDate());
    this.form.get('ytScheduledHour')?.setValue(dateNowHourSelection ?? HOUR_LIST[0]);
    this.form.get('ytScheduledMinute')?.setValue(dateNowMinuteSelection ?? MINUTE_LIST[0]);
    this.form.get('ytScheduledPeriod')?.setValue(dateNowTimePeriod ?? PERIOD_LIST[0]);

    // Generate available timezones and set current timezone
    this.timezones = Intl.supportedValuesOf('timeZone')?.map(timezone => {
      const timezoneValue = `${this.getTimezoneOffset(timezone)} (${timezone.replace('_', ' ')})`;
      return {
        key: timezone,
        value: timezoneValue,
      };
    });
    const dateNowTimezone = this.timezones.find(t => t.key === Intl.DateTimeFormat().resolvedOptions().timeZone);
    this.form.get('ytScheduledTimezone')?.setValue(dateNowTimezone ?? this.timezones[0].value);

    // Listen to changes in form values to hide and show the schedule
    this.form.get('ytManualSchedule')?.valueChanges.subscribe({
      next: (value: boolean) => {
        if (this.form.get('ytPostScheduleDate')?.value !== Visibility.Public) this.form.get('ytPostScheduleDate')?.setValue(null);
        else if (value) this.form.get('ytPostScheduleDate')?.setValue((new Date()).toISOString());
        this.setScheduleDisplayState(!value);
      },
    });
  }

  ngAfterViewInit(): void {
    this.form.get('ytPostTitle')?.setValue(this.operation?.title ?? '');
    this.form.get('ytPostDescription')?.setValue('');
  }

  get displayUploadScheduleSelect(): boolean {
    return this.form.get('ytPostVisibility')?.value === this.visibility.Private;
  }

  /**
   * A list of hour selection values.
   * @type {KeyValue<string, string>}
   * @memberof StepIntegrationYoutubeComponent
   */
  get scheduledHourList(): KeyValue<string, string>[] {
    return HOUR_LIST;
  }

  /**
   * A list of minute selection values.
   * @type {KeyValue<string, string>}
   * @memberof StepIntegrationYoutubeComponent
   */
  get scheduledMinuteList(): KeyValue<string, string>[] {
    return MINUTE_LIST;
  }

  /**
   * A list of time period selection values.
   * @type {KeyValue<string, string>}
   * @memberof StepIntegrationYoutubeComponent
   */
  get scheduledPeriodList(): KeyValue<string, string>[] {
    return PERIOD_LIST;
  }

  /**
   * A list of time period selection values.
   * @type {KeyValue<string, string>}
   * @memberof StepIntegrationYoutubeComponent
   */
  get scheduledTimezoneList(): KeyValue<string, string>[] {
    return this.timezones;
  }

  /**
   * Posts the check change event if the user wants to mark this video as being made for kids.
   * @param e
   * @memberof StepIntegrationYoutubeComponent
   */
  postMadeForKidsSelected(e: boolean): void {
    this.form.get('ytPostMadeForKids')?.setValue(e);
  }

  /**
   * Posts the check change event for whether the ODDR verified link should be
   * appended to the post description.
   * @param e
   * @memberof StepIntegrationYoutubeComponent
   */
  oddrLinkSelected(e: boolean): void {
    this.form.get('ytIncludeOddrLink')?.setValue(e);
  }

  /**
   * Posts the check change event if the user wants to post their media ASAP or set a schedule to the form.
   * @param e
   * @memberof StepIntegrationYoutubeComponent
   */
  postScheduleSelected(e: boolean): void {
    this.form.get('ytManualSchedule')?.setValue(e);
    this.constructAndSetDateTimeIso();
  }

  /**
   * Posts the visibility selection option for the media to the form.
   * @param e
   * @memberof StepIntegrationYoutubeComponent
   */
  postVisibilitySelected(e: Visibility): void {
    this.form.get('ytPostVisibility')?.setValue(e);
    if (e !== Visibility.Public) this.form.get('ytPostScheduleDate')?.setValue(null);
    else this.constructAndSetDateTimeIso();
  }

  /**
   * Posts the scheduled date for the media to be uploaded to the form.
   * @param e
   * @memberof StepIntegrationYoutubeComponent
   */
  scheduledDateSelected(e: NgbDate): void {
    this.form.get('ytScheduledYear')?.setValue(e.year);
    this.form.get('ytScheduledMonth')?.setValue(e.month);
    this.form.get('ytScheduledDay')?.setValue(e.day);
    this.constructAndSetDateTimeIso();
  }

  /**
   * Posts the scheduled time (hour) for the media to be uploaded to the form.
   * @param e
   * @memberof StepIntegrationYoutubeComponent
   */
  scheduledTimeHourSelected(e: KeyValue<string, string>): void {
    this.form.get('ytScheduledHour')?.setValue(e);
    this.constructAndSetDateTimeIso();
  }

  /**
   * Posts the scheduled time (minutes) for the media to be uploaded to the form.
   * @param e
   * @memberof StepIntegrationYoutubeComponent
   */
  scheduledTimeMinuteSelected(e: KeyValue<string, string>): void {
    this.form.get('ytScheduledMinute')?.setValue(e);
    this.constructAndSetDateTimeIso();
  }

  /**
   * Posts the scheduled period (am/pm) for the media to be uploaded to the form.
   * @param e
   * @memberof StepIntegrationYoutubeComponent
   */
  scheduledTimePeriodSelected(e: KeyValue<string, string>): void {
    this.form.get('ytScheduledPeriod')?.setValue(e);
    this.constructAndSetDateTimeIso();
  }

  /**
   * Posts the scheduled time zone for the media to be uploaded to the form.
   * @param e
   * @memberof StepIntegrationYoutubeComponent
   */
  scheduledTimezoneSelected(e: KeyValue<string, string>): void {
    // TODO:: Time period should be ordered and should be typeahead dropdown.
    this.form.get('ytScheduledTimezone')?.setValue(e);
    this.constructAndSetDateTimeIso();
  }

  /**
   * Sets the expanded and collapsed view of the media upload schedule form.
   * @param collapsed hides the media upload schedule form if `true`, shows on `false`.
   * @memberof StepIntegrationYoutubeComponent
   */
  setScheduleDisplayState(collapsed: boolean): void {
    if (!this.scheduleInCollapsedState) {
      this.scheduleIsCollapsing = true;
      setTimeout(() => {
        this.scheduleIsCollapsing = false;
        this.cdr.markForCheck();
      }, this.scheduleCollapseAnimationTime);
    }
    this.scheduleInCollapsedState = collapsed;
    this.cdr.markForCheck();
  }

  /**
   * Submits the integration payload and transitions to the next step.
   * @memberof StepIntegrationYoutubeComponent
   */
  submitAndGoToNextStep(): void {
    this.mediaUploadService.storeSelectedIntegrationPayload(this.constructAndStorePayloadForProvider()).subscribe({
      next: () => this.goToNextStep(),
    });
  }

  /**
   * Using the sorted formed data, constucts the date time schedule.
   * @private
   * @memberof StepIntegrationYoutubeComponent
   */
  private constructAndSetDateTimeIso(): void {
    // Ensure conversion back to 24 hour time format
    const hours = ((this.form.get('ytScheduledPeriod')?.value)?.key === 'pm') ?
      +((this.form.get('ytScheduledHour')?.value)?.value) + 12 :
      +((this.form.get('ytScheduledHour')?.value)?.value);
    const minutes = +(this.form.get('ytScheduledMinute')?.value)?.value;

    // Construct date
    const date = new Date(
      this.form.get('ytScheduledYear')?.value,
      this.form.get('ytScheduledMonth')?.value,
      this.form.get('ytScheduledDay')?.value,
      hours,
      minutes,
    );

    // Assign date as ISO string
    this.form.get('ytPostScheduleDate')?.setValue(date.toISOString());
  }

  /**
   * Constructs the required payload to setup the integration post.
   * @private
   * @returns the cleaned payload of form data.
   * @memberof StepIntegrationYoutubeComponent
   */
  private constructAndStorePayloadForProvider(): any {
    // Define config
    const addScheduleToPayload =
      this.form.get('ytPostVisibility')?.value === this.visibility.Private &&
      this.form.get('ytManualSchedule')?.value === false;
    const payload = {
      title: this.form.get('ytPostTitle')?.value as string,
      description: this.form.get('ytPostDescription')?.value as string,
      madeForKids: this.form.get('ytPostMadeForKids')?.value as string,
      visibility: this.form.get('ytPostVisibility')?.value as string,
      includeOddrLink: this.form.get('ytIncludeOddrLink')?.value as boolean,
      ...(addScheduleToPayload && {
        scheduledTime: this.form.get('ytPostScheduleDate')?.value as string,
      }),
    };
    return this.cleanIntegrationPayload(IntegrationProvider.YouTube, payload);
  }

  /**
   * Calculates and formats the time zone offset for a given date.
   * @private
   * @param timezone the time zone to add to the date.
   * @param date (optional) the date to convert. Will use the date time now if no date is provided.
   * @returns the time zone offset in UTC format.
   * @memberof StepIntegrationYoutubeComponent
   */
  private getTimezoneOffset(timezone: string, date: Date = new Date()): string {
    const formatter = new Intl.DateTimeFormat('en', {
      timeZone: timezone,
      timeZoneName: 'shortOffset', // e.g. 'GMT-5'
    });
    const parts = formatter.formatToParts(date);
    const offsetPart = parts.find(part => part.type === 'timeZoneName');
    return offsetPart ? offsetPart.value.replace('GMT', 'UTC') : 'UTC+0';
  }
}
